import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { DeviceHistory, DeviceUnitAsAt, DeviceVersion } from '@regigov/contracts';
import { DefinitionList, Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';

// ---- Version table ---------------------------------------------------------------

/**
 * Collapsed by default (decision 6): lists and search show current state only.
 * Every version is listed, including superseded ones, so the audit trail is complete.
 * The ordinal ("v3") is the entity's own count; the #id is the database row that a
 * correction must reference — the two are shown side by side so they are never confused.
 */
export function DeviceHistoryPanel({ queryKey, load, compliance = false, children }: { queryKey: unknown[]; load: () => Promise<DeviceHistory>; compliance?: boolean; children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const history = useQuery({ queryKey, queryFn: load, enabled: open });
  return (
    <details className="history" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>History</summary>
      {history.isLoading && <p className="muted">Loading history…</p>}
      <ProblemAlert error={history.error} />
      {history.data && (
        <>
          <p className="muted">
            <strong>True from/until</strong> is when the facts held in the real world; <strong>Recorded</strong> is when CER learned them. A correction supersedes the row it references and is
            shown with its own row id.
          </p>
          <VersionTable versions={history.data.versions} compliance={compliance} />
        </>
      )}
      {open && children}
    </details>
  );
}

const NOT_RECORDED = <span className="muted">Not recorded</span>;

/** Unit compliance status (C8); versions recorded before it existed don't carry it. */
const complianceOf = (v: DeviceVersion): ReactNode => (typeof v.data.complianceStatus === 'string' ? <StatusBadge status={v.data.complianceStatus} /> : NOT_RECORDED);

function VersionTable({ versions, compliance }: { versions: DeviceVersion[]; compliance: boolean }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Version</th>
          <th>Row id</th>
          <th>Status</th>
          {compliance && <th>Compliance</th>}
          <th>True from</th>
          <th>True until</th>
          <th>Recorded</th>
          <th>Changed by</th>
          <th>Source</th>
          <th>Reason</th>
          <th>Evidence</th>
          <th>Changes</th>
        </tr>
      </thead>
      <tbody>
        {versions.map((v) => (
          <tr key={v.versionId} className={v.supersededBy !== null ? 'muted' : undefined}>
            <td>v{v.version}</td>
            <td>
              <code>#{v.versionId}</code>
              {v.correctionOf !== null && <div className="muted">corrects #{v.correctionOf}</div>}
            </td>
            <td>{typeof v.data.status === 'string' && <StatusBadge status={v.data.status} />}</td>
            {compliance && <td>{complianceOf(v)}</td>}
            <td>{formatDateTime(v.validFrom)}</td>
            <td>{v.supersededBy !== null ? <em>superseded by #{v.supersededBy}</em> : v.validTo ? formatDateTime(v.validTo) : <em className="muted">current</em>}</td>
            <td>{formatDateTime(v.recordedAt)}</td>
            <td>{v.changedByName ?? <span className="muted">system</span>}</td>
            <td>
              {v.sourceChannel}
              {v.sourceParticipantName && <div className="muted">{v.sourceParticipantName}</div>}
            </td>
            <td>{v.changeReason}</td>
            <td>{v.evidenceRef && <code>{v.evidenceRef}</code>}</td>
            <td>{v.changes.length ? v.changes.join(', ') : v.correctionOf !== null ? 'No field changes' : 'Created'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---- As-at reconstruction --------------------------------------------------------

const toIso = (local: string): string | undefined => (local ? new Date(local).toISOString() : undefined);

export function AsAtPicker<T>({ queryKey, load, render }: { queryKey: unknown[]; load: (q: { asOf?: string; knownAt?: string }) => Promise<T>; render: (result: T) => ReactNode }) {
  const [asOf, setAsOf] = useState('');
  const [knownAt, setKnownAt] = useState('');
  const [query, setQuery] = useState<{ asOf?: string; knownAt?: string } | null>(null);
  const result = useQuery({ queryKey: [...queryKey, 'as-at', query], queryFn: () => load(query ?? {}), enabled: query !== null });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setQuery({ asOf: toIso(asOf), knownAt: toIso(knownAt) });
  };
  return (
    <section className="card">
      <h3>Reconstruct at a point in time</h3>
      <form className="form" onSubmit={submit}>
        <div className="grid2">
          <Field label="As of" hint="When the facts were true (blank = now)">
            <input type="datetime-local" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </Field>
          <Field label="As known at" hint="What CER had recorded by then (blank = now)">
            <input type="datetime-local" value={knownAt} onChange={(e) => setKnownAt(e.target.value)} />
          </Field>
        </div>
        <div>
          <button type="submit">Show</button>
        </div>
      </form>
      <ProblemAlert error={result.error} />
      {result.isLoading && <p className="muted">Loading…</p>}
      {result.data !== undefined && render(result.data)}
    </section>
  );
}

export function VersionSummary({ title, version, compliance = false }: { title: string; version: DeviceVersion | null; compliance?: boolean }) {
  if (!version) {
    return (
      <p className="muted">
        {title}: no version was true at that time (as then known).
      </p>
    );
  }
  const entries = Object.entries(version.data).filter(([k, v]) => v !== null && typeof v !== 'object' && !(compliance && k === 'complianceStatus'));
  return (
    <div className="card">
      <div className="row">
        <h4>{title}</h4>
        <span className="muted">
          v{version.version} · <code>#{version.versionId}</code>
          {version.correctionOf !== null && <> · corrects #{version.correctionOf}</>}
        </span>
      </div>
      <DefinitionList
        items={[
          ['True from', formatDateTime(version.validFrom)],
          ['True until', version.validTo ? formatDateTime(version.validTo) : 'open'],
          ['Recorded', formatDateTime(version.recordedAt)],
          ['Changed by', version.changedByName ?? version.sourceChannel],
          ['Reason', version.changeReason],
          ...entries.map(([k, v]) => [k, String(v)] as [string, ReactNode]),
          ...(compliance ? [['complianceStatus', complianceOf(version)] as [string, ReactNode]] : []),
        ]}
      />
    </div>
  );
}

export function UnitAsAtView({ result }: { result: DeviceUnitAsAt }) {
  return (
    <>
      <p className="muted">
        Facts as of {formatDateTime(result.asOf)}, as CER knew them at {formatDateTime(result.knownAt)}.
      </p>
      <VersionSummary title="Unit" version={result.unit} compliance />
      <VersionSummary title="Installation" version={result.installation} />
      <VersionSummary title="Model" version={result.model} />
      {result.owner ? (
        <div className="card">
          <div className="row">
            <h4>Owner</h4>
            <span className="muted">
              participant v{result.owner.version} · <code>#{result.owner.versionId}</code>
            </span>
          </div>
          <DefinitionList
            items={[
              ['Participant', `${result.owner.data.legalName} (${result.owner.data.id})`],
              ['Status', <StatusBadge status={result.owner.data.status} />],
            ]}
          />
        </div>
      ) : (
        <p className="muted">Owner: no participant version was true at that time (as then known).</p>
      )}
    </>
  );
}

// ---- Corrections -----------------------------------------------------------------

export type CorrectionField = { name: string; label: string; kind: 'text' | 'date' | 'json'; nullable?: boolean };

interface CorrectionProps<T> {
  queryKey: unknown[];
  load: () => Promise<DeviceHistory>;
  fields: CorrectionField[];
  submit: (body: { correctionOf: number; validFrom?: string; changes: Record<string, unknown>; changeReason: string; evidenceRef?: string }) => Promise<T>;
  onCorrected: (result: T) => void;
}

/**
 * CER-only. Picks the history row (by #id) whose facts were wrong, the field(s) to
 * fix, and records why with optional evidence. The old row is never edited; a new
 * row linked by `correctionOf` supersedes it.
 */
export function CorrectionForm<T>({ queryKey, load, fields, submit, onCorrected }: CorrectionProps<T>) {
  const history = useQuery({ queryKey, queryFn: load });
  const [target, setTarget] = useState('');
  const [field, setField] = useState(fields[0]?.name ?? '');
  const [value, setValue] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: submit,
    onSuccess: (r) => {
      setValue('');
      setReason('');
      setEvidence('');
      onCorrected(r);
    },
  });
  const correctable = (history.data?.versions ?? []).filter((v) => v.supersededBy === null);
  const selected = fields.find((f) => f.name === field);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    let parsed: unknown = value;
    if (selected.kind === 'json') {
      try {
        parsed = JSON.parse(value);
      } catch {
        setParseError('Value must be valid JSON');
        return;
      }
    } else if (value === '' && selected.nullable) parsed = null;
    setParseError(null);
    mutation.mutate({
      correctionOf: Number(target),
      validFrom: toIso(validFrom),
      changes: { [selected.name]: parsed },
      changeReason: reason,
      evidenceRef: evidence || undefined,
    });
  };

  return (
    <details className="panel">
      <summary>Correct a version</summary>
      <p className="muted">Corrections never overwrite: the chosen row stays in the audit trail and a new row supersedes it. Correcting an earlier version changes what was true then without touching the current record.</p>
      <ProblemAlert error={history.error} />
      <form className="form" onSubmit={onSubmit}>
        <div className="grid2">
          <Field label="Version to correct" hint="Identified by its history row id">
            <select value={target} onChange={(e) => setTarget(e.target.value)} required>
              <option value="">Select…</option>
              {correctable.map((v) => (
                <option key={v.versionId} value={v.versionId}>
                  #{v.versionId} — v{v.version}, true from {formatDateTime(v.validFrom)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Corrected facts true from" hint="Blank keeps the superseded version's date">
            <input type="datetime-local" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </Field>
          <Field label="Field">
            <select value={field} onChange={(e) => setField(e.target.value)}>
              {fields.map((f) => (
                <option key={f.name} value={f.name}>
                  {f.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Correct value" hint={selected?.kind === 'json' ? 'JSON object' : selected?.nullable ? 'Blank clears the value' : undefined}>
            {selected?.kind === 'json' ? (
              <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={3} required />
            ) : (
              <input type={selected?.kind === 'date' ? 'date' : 'text'} value={value} onChange={(e) => setValue(e.target.value)} required={!selected?.nullable} />
            )}
          </Field>
        </div>
        <Field label="Reason" hint="Mandatory; recorded against the correction">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} rows={2} />
        </Field>
        <Field label="Evidence reference" hint="e.g. document or case number">
          <input value={evidence} onChange={(e) => setEvidence(e.target.value)} />
        </Field>
        {parseError && (
          <div role="alert" className="alert">
            {parseError}
          </div>
        )}
        <ProblemAlert error={mutation.error} />
        <div>
          <button type="submit" disabled={mutation.isPending || !target}>
            Record correction
          </button>
        </div>
      </form>
    </details>
  );
}
